import React, { useState, useRef, useContext, useEffect } from 'react';
import axiosInstance from '../../../axiosInstance';
import { UserContext } from '../../../UserContext';
import { LoadingMessages } from '../../Utilities/LoadingMessages';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDate } from '../../Utilities/FormatDate.js';
import './Submissions.css';

const API_URL = process.env.REACT_APP_API_URL;

const Submissions = ({ setAlertMessage, alertMessage }) => {
  const { user, isGuest } = useContext(UserContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [progress, setProgress] = useState(0);
  const [previewImage, setPreviewImage] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const [fileError, setFileError] = useState('');
  const [imageError, setImageError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isImageDragging, setIsImageDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem('submissionDraft');
    return saved ? JSON.parse(saved) : {
      contentType: '',
      name: '',
      description: '',
      version: '1.0',
      category: '',
      utility: '',
      notes: '',
      badgeRequirement: 'NONE',
      status: 'DRAFT',
      thumbnailUrl: ''
    };
  });

  const categories = [
    'Animation', 'Audio', 'Stand-Alone-Asset', 'Mystery-Pack-PBR-Textures', 'Game-Ready-Experimental-Build' 
  ];

   const categoryToContentType = {
    'Animation': 'animations',
    'Audio': 'audio',
    'Stand-Alone-Asset': 'standalones',
    'Mystery-Pack-PBR-Textures': 'textures',
    'Game-Ready-Experimental-Build': 'builds'
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      validateAndSetFile(files[0]);
    }
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    setIsImageDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      validateAndSetImage(files[0]);
    }
  };


  const validateAndSetImage = async (file) => {
    setImageError('');
    
    if (!file.type.match('image.*')) {
      setImageError('Only image files are allowed');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setImageError('Image must be less than 5MB');
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      if (img.width < 1280 || img.height < 720) {
        setImageError('Image must be at least 1280x720 pixels');
        URL.revokeObjectURL(img.src);
        return;
      }
      
      if (thumbnailInputRef.current) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        thumbnailInputRef.current.files = dataTransfer.files;
        thumbnailInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
        handleThumbnailChange({ target: { files: dataTransfer.files } });
        URL.revokeObjectURL(img.src);
      }
    };
  };

  useEffect(() => {
    localStorage.setItem('submissionDraft', JSON.stringify(formData));
  }, [formData]);

  const steps = [
    { id: 0, title: "Introduction" },
    { id: 1, title: "Build Information" },
    { id: 2, title: "Release Settings" },
    { id: 3, title: "Files & Media" },
    { id: 4, title: "Review & Submit" }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      if (name === 'category') {
        newData.contentType = categoryToContentType[value] || '';
      }
      localStorage.setItem('submissionDraft', JSON.stringify(newData));
      return newData;
    });
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
        setFormData(prev => ({
          ...prev,
          thumbnailUrl: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBadgeSelect = (type) => {
    setFormData(prev => ({ ...prev, badgeRequirement: type }));
  };

   



  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionStatus(null);
    setProgress(0);

    try {
      const formPayload = new FormData();
      
      // Check if we have a selected file (from state)
      if (!selectedFile) {
        throw new Error('Please select a build file to upload');
      }
      
      // Add the file from state
      formPayload.append('file', selectedFile);
      
      // Add thumbnail if exists
      if (previewImage) {
        try {
          // Convert data URL back to File object
          const response = await fetch(previewImage);
          const blob = await response.blob();
          const thumbnailFile = new File([blob], "thumbnail.png", { 
            type: "image/png" 
          });
          
          // Append to FormData using the exact field name your backend expects
          formPayload.append('thumbnail', thumbnailFile);  // Remove the third parameter if not needed
          
          console.log('Thumbnail file appended:', thumbnailFile.name, thumbnailFile.size);
        } catch (error) {
          console.error('Error processing thumbnail:', error);
          // Optionally add error to your state to show user
          setImageError('Failed to process thumbnail image');
        }
      }

      // Add form data
      Object.keys(formData).forEach(key => {
        if (key !== 'thumbnailUrl') {
          formPayload.append(key, formData[key]);
        }
      });

      const response = await axiosInstance.post(
        `${API_URL}/upload/submissions/require-badgeId-process-meta-data`,
        formPayload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${user.token}`
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setProgress(percentCompleted);
            }
          }
        }
      );

      setSubmissionStatus({
        type: 'success',
        data: response.data
      });
      setAlertMessage({
      type: 'success',
      text: (
        <>
          <strong>{response.data.message}</strong>
          
        </>
      )
    });

    } catch (error) {
      console.error('Submission error:', error);
      setSubmissionStatus({
        type: 'error',
        message: error.response?.data?.error || error.message || 'Submission failed. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
      setProgress(0);
    }
  };

  // Update validateAndSetFile to use the new state
  const validateAndSetFile = (file) => {
    setFileError('');
    
    if (!file.name.endsWith('.zip')) {
      setFileError('Only ZIP files are allowed');
      return;
    }

    if (file.size > 2 * 1024 * 1024 * 1024) {
      setFileError('File size must be less than 2GB');
      return;
    }

    setSelectedFile(file); // Store the file in state
    
    if (fileInputRef.current) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      fileInputRef.current.files = dataTransfer.files;
      fileInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  // Update the file removal handler
  const handleRemoveFile = (e) => {
    e.stopPropagation();
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setFileError('');
  };
 
  // Update validateCurrentStep to check the state
  const validateCurrentStep = () => {
    switch(currentStep) {
      case 1:
        return formData.name && formData.description && formData.version && formData.category && formData.utility && formData.notes;
      case 2:
        return true;
      case 3:
        return !!selectedFile; // Check the state instead of the input ref
      default:
        return true;
    }
  };

  

  return (
    <motion.div 
      className="submissions-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="submission-header">
        <h2>Submit Your Experimental Build</h2>
        <p className="submission-tagline">
          Share your innovation with the community to get featured!
        </p>
      </div>

      <div className="stepper">
        {steps.map((step) => (
          <div 
            key={step.id} 
            className={`step ${currentStep >= step.id ? 'active' : ''} ${currentStep === step.id ? 'current' : ''}`}
          >
            <div className="step-circle">{step.id + 1}</div>
            <div className="step-text">{step.title}</div>
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -50, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="step-content"
        >
          {currentStep === 0 && (
            <div className="intro-step">
              <div className="submission-guidelines-card">
                <h3 className="section-title">Submission Guidelines</h3>
                <ul className="requirements-list">
                  <li>Complete project files (ZIP format, max 2GB).</li>
                  <li>Demo Reel (MP4 format, max 2GB).</li>
                  <li>Clear documentation of any experimental or non-standard features.</li>
                  <li>Disclosure of known stability issues.</li>
                  <li>Minimum prototype functionality suitable for testing, exploration, or illustrative deployment.</li>
                  <li>"De minimus" integration of third-party assets (if applicable).</li>
                   <li>
                    If third-party assets are included, the submission must reflect a use that aligns with 
                    <a href="https://www.law.cornell.edu/uscode/text/17/107" target="_blank" rel="noopener noreferrer"> 17 U.S.C. §107</a>, which includes:  <br /> 
                      <strong>
                        <br /><br />
                        <ol>
                          Adding new purpose, meaning, or utility (i.e. transformative use). <br /><br />
                          Avoiding direct market substitution or competition with the original asset.
                        </ol>
                      </strong>
                  </li>
                </ul>
                <span style={{ display: 'block', textAlign: 'justify', marginTop: '85px' }}><strong >WARNING:</strong> Fair use of <a href="https://www.law.cornell.edu/wex/de_minimis" target="_blank" rel="noopener noreferrer"><em> de minimis </em></a> third-party content in this context must extend beyond surface-level aesthetic modification, and must demonstrate a 
                  significant enhancement, adaptation, or purposeful recontextualization that alters the original asset’s functionality, 
                  use case, meaning, or purpose to such a degree that the submission does not act as a replacement or compete with the original asset's purpose or utility. If your submission adds new meaning, context, or function—and doesn’t replace or compete with the original asset’s reason for existing or functional capabilities—it aligns with fair use principles. That means your work should be clearly distinct from the original, with a different goal, audience, or use-case that transforms how people engage with it. While it may resemble a “derivative work,” fair use protects certain transformative uses without requiring permission or violating existing terms of service regarding copyright use restrictions. This framing is supported by legal precedent—including Campbell v. Acuff-Rose—which highlights that transformative works are less likely to cause market harm when they serve a new purpose.</span>

              </div>

              <motion.button
                className="nav-button submit-button"
                onClick={() => {
                if (isGuest()) {
                  setAlertMessage({            
                    type: 'warning',
                    text: 'You must signup or log in to use this feature.'
                  });
                  return;                       // stop here — no step change
                }

                setCurrentStep(1); // only runs for non-guests
              }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Start
              </motion.button>
            </div>
          )}

          {currentStep === 1 && (
            <div className="build-info-step">
              <h3 className="section-title">Build Information</h3>
              
              <div className="form-group">
                <label htmlFor="name">Build Name*</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., 'Realistic Baby Smiles v2.1'"
                />
                <p className="form-hint">Give your build a descriptive name.</p>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description*</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  placeholder="Describe your build's purpose, features, and requirements."
                  rows="4"
                />
                <p className="form-hint">Be detailed about what your build does.</p>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="version">Version*</label>
                  <input
                    type="text"
                    id="version"
                    name="version"
                    value={formData.version}
                    onChange={handleInputChange}
                    required
                    placeholder="1.0"
                  />
                  <p className="form-hint">Use semantic versioning (e.g., 1.0.2)</p>
                </div>

                <div className="form-group">
                  <label htmlFor="category">Category*</label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <p className="form-hint">Select the most relevant category.</p>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="utility">Intended Utility*</label>
                <input
                  type="text"
                  id="utility"
                  name="utility"
                  value={formData.utility}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., 'To use as a standalone animation or to enhance gameplay.'"
                />
                <p className="form-hint">This will be used to generate your build's unique URL</p>
              </div>

              <div className="form-group">
                <label htmlFor="notes">Technical Notes*</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  required
                  placeholder="No known issues, dependencies, or special instructions."
                  rows="3"
                />
                <p className="form-hint">Be honest about any limitations.</p>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="release-settings-step">
              <h3 className="section-title">Release Settings</h3>
              
              <div className="form-group">
                <label>Minimum Badge Requirement</label>
                <div className="badge-options">
                  <div 
                    className={`badge-option ${formData.badgeRequirement === 'NONE' ? 'selected' : ''}`}
                    onClick={() => handleBadgeSelect('NONE')}
                  >
                    <i className="fas fa-star"></i>
                    <span>Public (None)</span>
                  </div>
                  <div 
                    className={`badge-option ${formData.badgeRequirement === 'DIAMOND' ? 'selected' : ''}`}
                    onClick={() => handleBadgeSelect('DIAMOND')}
                  >
                    <i className="fas fa-gem"></i>
                    <span>Diamond</span>
                  </div>
                  <div 
                    className={`badge-option ${formData.badgeRequirement === 'FOUNDER' ? 'selected' : ''}`}
                    onClick={() => handleBadgeSelect('FOUNDER')}
                  >
                    <i className="fas fa-crown"></i>
                    <span>Founder</span>
                  </div>
                </div>
                <p className="form-hint">Restrict access to supporters with certain badges</p>
              </div>

              <div className="form-group">
                <label htmlFor="status">Initial Status</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="REVIEW">Submit for Review (Featured Release)</option>
                  <option value="PUBLISHED">Publish Immediately (On-Demand)</option>
                </select>
                <p className="form-hint">Control when your build becomes visible</p>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="files-step">
              <h3 className="section-title">Files & Media</h3>
              
              <div className="form-group">
                <label htmlFor="file">Build Files (ZIP)*</label>
                <div 
                  className={`file-upload-box ${isDragging ? 'dragging' : ''}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleFileDrop}
                >
                  <input
                    type="file"
                    id="file"
                    ref={fileInputRef}
                    accept=".zip"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        validateAndSetFile(e.target.files[0]);
                      }
                    }}
                    required
                  />
                  <div className="upload-prompt">
                    <i className="upload-icon">📁</i>
                    <p>Drag & drop your ZIP file here or click to browse</p>
                    <p className="file-hint">Max 2GB. Only .zip files accepted.</p>
                    
                    {/* REPLACE THIS SECTION */}
                    {selectedFile && (
                      <div className="file-preview">
                        <span>{selectedFile.name}</span>
                        <button 
                          type="button"
                          className="remove-file"
                          onClick={handleRemoveFile}
                        >
                          ×
                        </button>
                      </div>
                    )}
                    {fileError && <p className="error-message">{fileError}</p>}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="thumbnail">Thumbnail (1280x720 minimum)*</label>
                <div 
                  className={`thumbnail-upload-box ${isImageDragging ? 'dragging' : ''}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsImageDragging(true);
                  }}
                  onDragLeave={() => setIsImageDragging(false)}
                  onDrop={handleImageDrop}
                >
                  <input
                    type="file"
                    id="thumbnail"
                    ref={thumbnailInputRef}
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        validateAndSetImage(e.target.files[0]);
                      }
                    }}
                  />
                  <div className="upload-prompt">
                    <i className="upload-icon">🖼️</i>
                    <p>Drag & drop your thumbnail here or click to browse</p>
                    <p className="file-hint">1280x720 minimum, PNG/JPG, max 5MB</p>
                    {previewImage ? (
                      <div className="thumbnail-preview">
                        <img src={previewImage} alt="Thumbnail preview" />
                        <button 
                          type="button" 
                          className="remove-thumbnail"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewImage(null);
                            thumbnailInputRef.current.value = '';
                            setImageError('');
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="placeholder">
                        No thumbnail selected
                      </div>
                    )}
                    {imageError && <p className="error-message">{imageError}</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="review-step">
              <h3 className="section-title">Review Your Submission</h3>
              
              <div className="submission-preview">
                <div className="preview-header">
                  <h4>This is how your submission will appear:</h4>
                </div>
                
                <div className="preview-content">
                  <div className="preview-media">
                    {previewImage ? (
                      <img 
                        src={previewImage} 
                        alt="Submission preview" 
                        className="preview-thumbnail"
                      />
                    ) : (
                      <div className="preview-thumbnail placeholder">
                        <span>No thumbnail</span>
                      </div>
                    )}
                    <div className="preview-badge">
                      {formData.status === 'REVIEW' ? 'Featured' : 'On-Demand'}
                    </div>
                  </div>

                  <div className="featured-details">
                    <div className="detail-header">
                      <h2>{formData.name || "Your Build Name"}</h2>
                      <div className="release-tags">
                        <span className="tag">Latest</span>
                        <span className="tag">Version {formData.version || "1.0"}</span>
                      </div>
                    </div>
                    
                    <div className="detail-meta">
                      <p className="release-description">
                        {formData.description || "Your build description will appear here"}
                      </p>
                    </div>
                    
                    <div className="meta-row">
                      <div className="meta-item">
                        <i className="icon-calendar"></i>
                        <span>Released: {formatDate(new Date())}</span>
                      </div>
                      <div className="meta-item">
                        <i className="icon-file"></i>
                        <span>File size: {formData?.fileSize || "245 MB"}</span>
                      </div>
                    </div>
                    
                    <div className="meta-item category-item">
                      <i className="fas fa-tag"></i>
                      <span>{formData.category || "Category"}</span>
                    </div>
                    
                    <div className="meta-item notes-item">
                      <i className="icon-alert"></i>
                      <span className='small-text'>{formData.notes || "No technical notes provided"}</span>
                    </div>

                    <div className="preview-download">
                      {formData.badgeRequirement === 'NONE' ? (
                        <button className="download-button">
                          <i className="fas fa-download"></i> Download Now
                        </button>
                      ) : (
                        <button className="download-button locked">
                          <i className="fas fa-lock"></i> Unlock with {formData.badgeRequirement === 'DIAMOND' ? 'Diamond' : 'Founder'} Badge
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
  
                
              {isSubmitting && (
                <motion.div 
                  className="upload-progress"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <LoadingMessages />
                  <div className="progress-container">
                    <progress value={progress} max="100">{progress}%</progress>
                    <span>{progress}%</span>
                  </div>
                  <p className="upload-tip">Uploading your build... This might take a while for large files.</p>
                </motion.div>
              )}

              {submissionStatus && (
                <motion.div 
                  className={`submission-status ${submissionStatus.type}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="status-icon">
                    {submissionStatus.type === 'success' ? '✓' : '⚠️'}
                  </div>
                  <div className="status-message">
                    {submissionStatus.message}
                    {submissionStatus.type === 'success' && (
                      <div className="success-details">
                        <p>Submission ID: {submissionStatus.data?.content?.slug}</p>
                         <p><strong>Status:</strong> {submissionStatus.data?.content?.status}</p>
                        <p><strong>Name:</strong> {submissionStatus.data?.content?.name}</p>
                        <p><strong>Release Date:</strong> {formatDate(new Date(submissionStatus.data?.content?.releaseDate))}</p>
                        
                      </div>
                     
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="step-navigation">
        {currentStep > 0 && (
          <motion.button
            className="nav-button prev-button"
            onClick={() => setCurrentStep(prev => prev - 1)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {currentStep === 4 ? 'Back to Files' : 'Previous'}
          </motion.button>
        )}

        {currentStep < 3 && currentStep > 0 && (
          <motion.button
            className="nav-button next-button"
            onClick={() => setCurrentStep(prev => prev + 1)}
            disabled={!validateCurrentStep()}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Next
          </motion.button>
        )}

        {currentStep === 3 && (
          <motion.button
            className="nav-button submit-button"
            onClick={() => setCurrentStep(4)}
            disabled={!validateCurrentStep()}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Review
          </motion.button>
        )}

        {currentStep === 4 && (
          <motion.button
            className="nav-button submit-button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </motion.button>
        )}
      </div>

      <div className="submission-terms">
      </div>
    </motion.div>
  );
};

export default Submissions;